import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { z } from "zod";
import { LucideDynamicIcon } from "@lucide/angular";
import { environment } from "@/environments/environment";
import { AuthService, User } from "../core/auth.service";
import { HUB_OPTIONS, type HubKey } from "../core/hubs";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import {
  buttonClasses,
  cardClasses,
  inputClasses,
  labelClasses,
} from "../shared/ui";

const emailSchema = z.string().email("Please enter a valid email address");
const passwordSchema = z
  .string()
  .min(6, "Password must be at least 6 characters");
const AUTH_PAGE_DEBUG_PREFIX = "[LINA][auth-page]";

function logAuthPageStep(step: string, details?: unknown) {
  if (!environment.production) {
    console.log(`${AUTH_PAGE_DEBUG_PREFIX} ${step}`, details ?? "");
  }
}

@Component({
  selector: "app-auth-page",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LanguageSelectorComponent,
    LucideDynamicIcon,
  ],
  template: `
    <div
      *ngIf="authLoading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="loader-circle"
        class="h-8 w-8 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div
      *ngIf="!authLoading"
      class="min-h-screen flex flex-col items-center justify-center p-4 ocean-gradient relative overflow-hidden"
    >
      <div class="absolute right-4 top-4 z-20">
        <app-language-selector [compact]="true" className="bg-background/80" />
      </div>

      <div class="absolute top-10 left-10 text-6xl animate-float opacity-60">
        🏝️
      </div>
      <div class="absolute top-20 right-20 text-4xl animate-wave opacity-50">
        ⛵
      </div>
      <div
        class="absolute bottom-20 left-20 text-5xl animate-float opacity-40"
        style="animation-delay: 1s"
      >
        🌴
      </div>
      <div
        class="absolute bottom-10 right-10 text-6xl animate-wave opacity-50"
        style="animation-delay: 0.5s"
      >
        🦀
      </div>

      <div class="text-center mb-8 relative z-10">
        <div
          class="mb-4 flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3"
        >
          <svg
            lucideIcon="ship"
            class="h-12 w-12 text-primary-foreground"
          ></svg>
          <div
            class="flex max-w-[min(44rem,calc(100vw-2rem))] flex-col items-center gap-1 sm:items-start"
          >
            <h1
              class="text-4xl font-bold text-primary-foreground drop-shadow-lg"
            >
              LINA
            </h1>
            <span
              class="text-center text-sm font-semibold uppercase leading-snug text-primary-foreground/90 drop-shadow sm:text-left sm:text-base"
            >
              {{ language.t("brand.fullName") }}
            </span>
          </div>
        </div>
        <p class="text-lg text-primary-foreground/90 max-w-md">
          {{ language.t("auth.subtitle") }}
        </p>
      </div>

      <section
        [class]="
          cardClasses('w-full max-w-md shadow-2xl relative z-10 border-0')
        "
      >
        <header class="text-center space-y-1.5 p-6 pb-4">
          <div class="flex justify-center mb-2">
            <div class="p-3 rounded-full bg-primary/10">
              <svg lucideIcon="anchor" class="h-8 w-8 text-primary"></svg>
            </div>
          </div>
          <h2 class="text-2xl font-semibold leading-none tracking-normal">
            {{ language.t("auth.title") }}
          </h2>
          <p class="text-sm text-muted-foreground">
            {{
              activeTab === "login"
                ? language.t("auth.loginDescription")
                : language.t("auth.signupDescription")
            }}
          </p>
        </header>

        <div class="p-6 pt-0">
          <div class="grid w-full grid-cols-2 mb-6 rounded-md bg-muted p-1">
            <button
              type="button"
              (click)="activeTab = 'login'"
              [class]="tabClass('login')"
            >
              <svg lucideIcon="waves" class="h-4 w-4"></svg>
              {{ language.t("auth.login") }}
            </button>
            <button
              type="button"
              (click)="activeTab = 'signup'"
              [class]="tabClass('signup')"
            >
              <svg lucideIcon="ship" class="h-4 w-4"></svg>
              {{ language.t("auth.signUp") }}
            </button>
          </div>

          <form
            *ngIf="activeTab === 'login'"
            (ngSubmit)="handleLogin()"
            class="space-y-4"
          >
            <div class="space-y-2">
              <label [class]="labelClasses()" for="login-email">{{
                language.t("auth.email")
              }}</label>
              <input
                [class]="inputClasses()"
                id="login-email"
                name="loginEmail"
                type="email"
                placeholder="captain@island.com"
                [(ngModel)]="loginEmail"
                required
                [disabled]="isLoading"
              />
            </div>
            <div class="space-y-2">
              <label [class]="labelClasses()" for="login-password">{{
                language.t("auth.password")
              }}</label>
              <input
                [class]="inputClasses()"
                id="login-password"
                name="loginPassword"
                type="password"
                placeholder="••••••••"
                [(ngModel)]="loginPassword"
                required
                [disabled]="isLoading"
              />
            </div>
            <button
              type="submit"
              [class]="buttonClasses('default', 'lg', 'w-full')"
              [disabled]="isLoading"
            >
              <svg
                *ngIf="isLoading"
                lucideIcon="loader-circle"
                class="mr-2 h-4 w-4 animate-spin"
              ></svg>
              <svg
                *ngIf="!isLoading"
                lucideIcon="anchor"
                class="mr-2 h-4 w-4"
              ></svg>
              {{
                isLoading
                  ? language.t("auth.loggingIn")
                  : language.t("auth.login")
              }}
            </button>
          </form>

          <form
            *ngIf="activeTab === 'signup'"
            (ngSubmit)="handleSignup()"
            class="space-y-4"
          >
            <div class="space-y-2">
              <label [class]="labelClasses()" for="display-name">{{
                language.t("auth.explorerName")
              }}</label>
              <input
                [class]="inputClasses()"
                id="display-name"
                name="displayName"
                type="text"
                placeholder="Captain Jack"
                [(ngModel)]="displayName"
                [disabled]="isLoading"
              />
            </div>
            <div class="space-y-2">
              <label [class]="labelClasses()" for="signup-hub">{{
                language.t("auth.hub")
              }}</label>
              <select
                [class]="inputClasses()"
                id="signup-hub"
                name="signupHub"
                [(ngModel)]="signupHub"
                required
                [disabled]="isLoading"
              >
                <option value="">{{ language.t("auth.chooseHub") }}</option>
                <option *ngFor="let hub of hubOptions" [value]="hub.key">
                  {{ hub.label }}
                </option>
              </select>
            </div>
            <div class="space-y-2">
              <label [class]="labelClasses()" for="signup-email">{{
                language.t("auth.email")
              }}</label>
              <input
                [class]="inputClasses()"
                id="signup-email"
                name="signupEmail"
                type="email"
                placeholder="explorer@island.com"
                [(ngModel)]="signupEmail"
                required
                [disabled]="isLoading"
              />
            </div>
            <div class="space-y-2">
              <label [class]="labelClasses()" for="signup-password">{{
                language.t("auth.password")
              }}</label>
              <input
                [class]="inputClasses()"
                id="signup-password"
                name="signupPassword"
                type="password"
                placeholder="••••••••"
                [(ngModel)]="signupPassword"
                required
                [disabled]="isLoading"
              />
            </div>
            <div class="space-y-2">
              <label [class]="labelClasses()" for="confirm-password">{{
                language.t("auth.confirmPassword")
              }}</label>
              <input
                [class]="inputClasses()"
                id="confirm-password"
                name="signupConfirmPassword"
                type="password"
                placeholder="••••••••"
                [(ngModel)]="signupConfirmPassword"
                required
                [disabled]="isLoading"
              />
            </div>
            <button
              type="submit"
              [class]="buttonClasses('default', 'lg', 'w-full')"
              [disabled]="isLoading"
            >
              <svg
                *ngIf="isLoading"
                lucideIcon="loader-circle"
                class="mr-2 h-4 w-4 animate-spin"
              ></svg>
              <svg
                *ngIf="!isLoading"
                lucideIcon="ship"
                class="mr-2 h-4 w-4"
              ></svg>
              {{
                isLoading
                  ? language.t("auth.signingUp")
                  : language.t("auth.joinAdventure")
              }}
            </button>
          </form>
        </div>
      </section>

      <p
        class="mt-8 text-sm text-primary-foreground/70 text-center relative z-10"
      >
        {{ language.t("auth.footer") }}
      </p>
    </div>
  `,
})
export class AuthPageComponent implements OnInit, OnDestroy {
  readonly auth = inject(AuthService);
  readonly language = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  authLoading = true;
  isLoading = false;
  activeTab: "login" | "signup" = "login";
  loginEmail = "";
  loginPassword = "";
  signupEmail = "";
  signupPassword = "";
  signupConfirmPassword = "";
  displayName = "";
  signupHub: HubKey | "" = "";
  readonly hubOptions = HUB_OPTIONS;

  private subscription?: Subscription;

  ngOnInit() {
    logAuthPageStep("ngOnInit");
    this.subscription = this.auth.state$.subscribe((state) => {
      logAuthPageStep("authState", {
        loading: state.loading,
        hasUser: Boolean(state.user),
        userId: state.user?.id ?? null,
      });
      this.authLoading = state.loading;
      this.changeDetector.detectChanges();

      if (state.user) {
        logAuthPageStep("navigate", { target: "/" });
        void this.router.navigateByUrl("/");
      }
    });
  }

  ngOnDestroy() {
    logAuthPageStep("ngOnDestroy");
    this.subscription?.unsubscribe();
  }

  async handleLogin() {
    try {
      emailSchema.parse(this.loginEmail);
      passwordSchema.parse(this.loginPassword);
    } catch (err) {
      if (err instanceof z.ZodError) {
        this.toast.error(err.errors[0].message);
        return;
      }
    }

    this.isLoading = true;
    this.changeDetector.detectChanges();
    const { error } = await this.auth.signIn(
      this.loginEmail,
      this.loginPassword,
    );
    this.isLoading = false;
    this.changeDetector.detectChanges();

    if (error) {
      this.toast.error(
        error.message.includes("Invalid login credentials")
          ? this.language.t("auth.invalidCredentials")
          : error.message,
      );
    } else {
      this.toast.success(this.language.t("auth.welcomeBack"));
      await this.router.navigateByUrl("/");
    }
  }

  async handleSignup() {
    try {
      emailSchema.parse(this.signupEmail);
      passwordSchema.parse(this.signupPassword);
    } catch (err) {
      if (err instanceof z.ZodError) {
        this.toast.error(err.errors[0].message);
        return;
      }
    }

    if (this.signupPassword !== this.signupConfirmPassword) {
      this.toast.error(this.language.t("auth.passwordsDoNotMatch"));
      return;
    }

    if (!this.signupHub) {
      this.toast.error(this.language.t("auth.hubRequired"));
      return;
    }

    this.isLoading = true;
    this.changeDetector.detectChanges();
    const { error } = await this.auth.signUp(
      this.signupEmail,
      this.signupPassword,
      this.displayName,
      this.signupHub,
    );
    this.isLoading = false;
    this.changeDetector.detectChanges();

    if (error) {
      this.toast.error(
        error.message.includes("already registered")
          ? this.language.t("auth.emailRegistered")
          : error.message,
      );
    } else {
      this.language.setLanguage("en");
      this.toast.success(this.language.t("auth.welcomeAboard"));
      await this.router.navigateByUrl("/tutorial");
    }
  }

  tabClass(value: "login" | "signup") {
    return [
      "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
      this.activeTab === value
        ? "bg-background text-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground",
    ].join(" ");
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  inputClasses = inputClasses;
  labelClasses = labelClasses;
}
